"use client";

import { useEffect } from "react";
import { NonStandardInfo } from "@/components/configurator/NonStandardInfo";
import { ConfiguratorFooter } from "@/components/configurator/ConfiguratorFooter";
import { chipStyle } from "@/components/configurator/chip-style";
import { useConfigurator } from "@/store/configurator";

interface Props {
  onNext: () => void;
  onBack: () => void;
}

export function StringCountStep({ onNext, onBack }: Props) {
  const { state, referenceData, setStringCount, setScaleLength } = useConfigurator();

  const instrument = referenceData.instruments.find(
    (entry) => entry.slug === state.instrumentId
  );
  const counts = instrument?.stringCounts ?? [];
  const scales = instrument?.scaleLengths ?? [];
  const showScales = scales.length > 1;
  const defaultScaleId =
    scales.find((scale) => scale.isDefault)?.id ??
    scales[0]?.id ??
    null;
  // Smart default: preselect the standard string count so the user can Continue
  // straight through without making every sub-choice manually (still editable).
  const defaultCount = counts.find((count) => count.isStandard) ?? counts[0];

  useEffect(() => {
    if (!state.scaleLengthId && defaultScaleId) {
      setScaleLength(defaultScaleId);
    }
  }, [defaultScaleId, setScaleLength, state.scaleLengthId]);

  useEffect(() => {
    if (!state.stringCountId && defaultCount) {
      setStringCount(defaultCount.id, defaultCount.count, !defaultCount.isStandard);
    }
  }, [defaultCount, setStringCount, state.stringCountId]);

  const canProceed = !!state.stringCountId && !!state.scaleLengthId;

  return (
    <div>
      <h2
        className="text-2xl font-black mb-2"
        style={{ fontFamily: "var(--font-display)" }}
      >
        How many strings?
      </h2>
      <div className="mb-8 flex items-center gap-2 text-sm" style={{ color: "#9A9A9A" }}>
        <span>Choose the count and scale length for this build.</span>
        <NonStandardInfo />
      </div>

      <div className="mb-8 grid gap-6 md:grid-cols-2">
        <section>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#8A8A8A" }}>
            String Count
          </p>
          <div className="flex flex-wrap gap-2">
          {counts.map((count) => {
            const selected = state.stringCountId === count.id;
            return (
              <button
                key={count.id}
                type="button"
                onClick={() => setStringCount(count.id, count.count, !count.isStandard)}
                aria-pressed={selected}
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-sm border transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                style={chipStyle(selected)}
              >
                {count.label}
                {!count.isStandard ? (
                  <span
                    className="text-[10px] font-semibold uppercase tracking-wider"
                    style={{ color: selected ? "#E8C547" : "#8A8A8A" }}
                  >
                    Special
                  </span>
                ) : null}
              </button>
            );
          })}
          </div>
        </section>

        <section>
          <p className="text-xs font-semibold uppercase tracking-widest mb-3" style={{ color: "#8A8A8A" }}>
            Scale Length
          </p>
          <div className="flex flex-wrap gap-2">
            {showScales && scales.map((scale) => {
              const selected = state.scaleLengthId === scale.id;
              return (
                <button
                  key={scale.id}
                  type="button"
                  onClick={() => setScaleLength(scale.id)}
                  aria-pressed={selected}
                  className="px-5 py-2.5 text-sm font-medium rounded-sm border transition-all focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E8C547]"
                  style={chipStyle(selected)}
                >
                  {scale.name}
                  {scale.isDefault && !selected ? (
                    <span className="ml-2 text-xs" style={{ color: "#8A8A8A" }}>
                      default
                    </span>
                  ) : null}
                </button>
              );
            })}
            {!showScales && scales[0] ? (
              <span
                className="px-5 py-2.5 text-sm font-medium rounded-sm border"
                style={{ borderColor: "#2A2A2A", backgroundColor: "#141414", color: "#9A9A9A" }}
              >
                {scales[0].name}
              </span>
            ) : null}
          </div>
        </section>
      </div>

      <ConfiguratorFooter
        onBack={onBack}
        onNext={onNext}
        nextDisabled={!canProceed}
        nextHint="Choose a string count and scale length to continue"
      />
    </div>
  );
}
